import type { PayrollTeam } from "@/payroll/roster/types";
import { formatRosterValue } from "@/payroll/roster/utils";
import type {
  EnrichedRosterTeam,
  EnrichedRosterView,
  RosterView,
} from "./roster-types";

const teamNames = (teams: PayrollTeam[]) =>
  teams.map((team) => team.name).join(", ") || "No team";

const markChangedTeams = (
  teams: PayrollTeam[],
  changed: PayrollTeam[],
): EnrichedRosterTeam[] => {
  const changedIds = new Set(changed.map((team) => team.id));
  return teams.map((team) => ({ ...team, changed: changedIds.has(team.id) }));
};

export const enrichRoster = (roster: RosterView): EnrichedRosterView => {
  const members = roster.members.map((member) => ({
    ...member,
    teamNames: teamNames(member.team_access_keys),
    teamChange: member.team_change
      ? {
          before: markChangedTeams(
            member.team_change.before,
            member.team_change.removed,
          ),
          after: markChangedTeams(
            member.team_change.after,
            member.team_change.added,
          ),
        }
      : null,
  }));
  const showTeams = members.some(
    (member) =>
      member.team_access_keys.length > 0 ||
      (member.teamChange?.before.length ?? 0) > 0 ||
      (member.teamChange?.after.length ?? 0) > 0,
  );

  return {
    ...roster,
    members,
    showTeams,
    // Search the words shown on the row, including previous values.
    searchEntries: members.map((member) => ({
      id: member.id,
      text: [
        member.display_name,
        ...(showTeams ? [member.teamNames] : []),
        ...(showTeams && member.teamChange
          ? [
              teamNames(member.teamChange.before),
              teamNames(member.teamChange.after),
            ]
          : []),
        ...roster.fields.flatMap((field) => {
          const change = member.field_changes[field.key];
          return [
            formatRosterValue(member.values[field.key], field),
            ...(change
              ? [
                  formatRosterValue(change.before, field),
                  formatRosterValue(change.after, field),
                ]
              : []),
          ];
        }),
      ].join(" "),
    })),
  };
};
